import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { ApiPost } from "../../helpers/API/ApiData";
import { toast } from "react-toastify";
import * as authUtil from "../../utils/auth.util";
import * as userUtil from "../../utils/user.util";

interface FirstLoginPasswordChangeProps {
  open: boolean;
  onClose: () => void;
  adminInfo: any;
}

const FirstLoginPasswordChange: React.FC<FirstLoginPasswordChangeProps> = ({
  open,
  onClose,
  adminInfo,
}) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors: any = {};
    let isValid = true;

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Aktuelles Passwort ist erforderlich";
      isValid = false;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "Neues Passwort ist erforderlich";
      isValid = false;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Passwort muss mindestens 8 Zeichen lang sein";
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = "Passwort muss Groß- und Kleinbuchstaben sowie Zahlen enthalten";
      isValid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Passwort bestätigen ist erforderlich";
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwörter stimmen nicht überein";
      isValid = false;
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = "Neues Passwort muss sich vom aktuellen unterscheiden";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response: any = await ApiPost("admin/change-password-first-login", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (response?.status === 200) {
        toast.success("Passwort erfolgreich geändert!");

        // Update admin info to remove requirePasswordChange flag
        const updatedAdminInfo = {
          ...adminInfo,
          requirePasswordChange: false,
        };
        userUtil.setAdminInfo(updatedAdminInfo);

        // Close modal and redirect
        onClose();

        // Redirect based on role
        if (adminInfo?.role === "chatbot") {
          window.location.href = "/message-center";
        } else {
          window.location.href = "/dashboard";
        }
      } else {
        toast.error(response?.data?.message || "Fehler beim Ändern des Passworts");
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Fehler beim Ändern des Passworts. Bitte versuchen Sie es erneut."
      );
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="h3" sx={{ mb: 0 }}>
            Passwort bei erster Anmeldung ändern
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Sie müssen Ihr temporäres Passwort ändern, um fortzufahren.
        </Alert>

        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <Box sx={{ mb: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
              Aktuelles temporäres Passwort
            </Typography>
            <TextField
              fullWidth
              type={showCurrentPassword ? "text" : "password"}
              variant="outlined"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Geben Sie Ihr temporäres Passwort ein"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* New Password */}
          <Box sx={{ mb: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
              Neues Passwort
            </Typography>
            <TextField
              fullWidth
              type={showNewPassword ? "text" : "password"}
              variant="outlined"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Geben Sie ein neues Passwort ein"
              error={!!errors.newPassword}
              helperText={errors.newPassword || "Mindestens 8 Zeichen mit Groß- und Kleinbuchstaben und Zahlen"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Confirm Password */}
          <Box sx={{ mb: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
              Neues Passwort bestätigen
            </Typography>
            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Bestätigen Sie Ihr neues Passwort"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="brand"
              size="large"
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Wird gespeichert...
                </>
              ) : (
                "Passwort ändern"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FirstLoginPasswordChange;
